import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Clock, LogIn, LogOut, MapPin, RefreshCw, Smartphone, UserPlus } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import { formatClinicDate, formatClinicTime } from '../../utils/clinicTime';
import PunchDialog from './PunchDialog';
import PunchCodeDialog from './PunchCodeDialog';
import MarkForColleagueDialog from './MarkForColleagueDialog';
import MyLeaveCard from './MyLeaveCard';
import { STATUS_LEGEND, datePart, dayFlags, describeDay, describeSchedule, formatHours, statusOf } from './attendanceStatus';
import { FEATURES } from '../../config/features';

// The signed-in person's own attendance: punch in and out with a selfie and the phone's position,
// today's status, and this week at a glance. Built for a phone first; it is where staff punch.

const time = (value) => formatClinicTime(value, { hour: 'numeric', minute: '2-digit', hour12: true }, '');

// Tailwind's `sm`, the same line the phone button has always been drawn on, so one query decides
// both halves of the choice below.
const useWideScreen = () => {
  const [wide, setWide] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(min-width: 640px)').matches : true),
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 640px)');
    const onChange = (e) => setWide(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return wide;
};

const FLAG_TONE = {
  amber: 'bg-amber-50 text-amber-800 border-amber-200',
  red: 'bg-red-50 text-red-800 border-red-200',
  gray: 'bg-gray-50 text-gray-700 border-gray-200',
};

const MyAttendance = () => {
  const notify = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [punching, setPunching] = useState(null); // 'in' | 'out'
  // The same punch, handed to the person's own phone by a scanned code. Offered beside the button
  // because a desktop has no camera and no real position; pointless on a phone, where the button
  // itself already does the job, so it is hidden on small screens.
  const [codeFor, setCodeFor] = useState(null); // 'IN' | 'OUT'
  // Starting a colleague's record when they cannot reach their own account. Arriving or leaving is
  // asked inside that dialog: this person's own shift says nothing about theirs.
  const [markingForColleague, setMarkingForColleague] = useState(false);

  // Where this person may punch from. On a phone the button is the whole point; on a desktop it is
  // only offered while FEATURES.punchFromDesktop is on, because a desktop's selfie and position are
  // not worth recording. With it off, the desktop's only route is to hand the punch to the phone.
  const wide = useWideScreen();
  const mayPunchHere = FEATURES.punchFromDesktop || !wide;

  const load = useCallback(async () => {
    try {
      const res = await attendanceAPI.mine();
      setData(res);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Your attendance could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Hours tick while punched in: look again every minute.
  useEffect(() => {
    if (!data?.open) return undefined;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [data?.open, load]);

  const punch = async (blob, position) => {
    const entry = punching === 'in'
      ? await attendanceAPI.punchIn(blob, position)
      : await attendanceAPI.punchOut(blob, position);
    const side = punching;
    setPunching(null);
    const at = side === 'in' ? entry?.clockInAt : entry?.clockOutAt;
    const flagged = entry?.reviewStatus === 'FLAGGED';
    const why = entry?.[side]?.approximate
      ? 'Your phone shared only an approximate location, so it will be reviewed.'
      : 'Outside the hospital, so it will be reviewed.';
    notify(flagged ? 'warning' : 'success', `Punched ${side} at ${time(at)}${flagged ? `. ${why}` : '.'}`);
    load();
    window.dispatchEvent(new Event('clinic:attendance-changed'));
  };

  const open = data?.open || null;
  const today = data?.today || null;
  const hospital = data?.hospital || { configured: false };
  const todayStatus = statusOf(today);

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-4 sm:p-6">
      <div className="max-w-md mx-auto space-y-4">
        <div className="bg-white rounded-2xl shadow-xl p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Clock className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">My Attendance</h1>
            <p className="text-sm text-gray-600">{formatClinicDate(new Date(), { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl shadow-xl p-10 text-center">
            <RefreshCw className="h-6 w-6 text-emerald-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading…</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-sm text-red-800 flex items-start justify-between gap-3">
            <span>{error}</span>
            <button type="button" onClick={load} className="font-medium underline">Try again</button>
          </div>
        ) : (
          <>
            <section aria-label="Today" className="bg-white rounded-2xl shadow-xl p-5 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Today</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {open ? `In since ${time(open.clockInAt)}` : describeDay(today) || 'Not in yet'}
                  </p>
                </div>
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${todayStatus.chip}`}>
                  {open ? 'Punched in' : todayStatus.label || 'Not in yet'}
                </span>
              </div>

              <dl className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-gray-50 p-3">
                  <dt className="text-xs text-gray-500">In</dt>
                  <dd className="font-semibold text-gray-900">{time(today?.clockInAt) || '–'}</dd>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <dt className="text-xs text-gray-500">Out</dt>
                  <dd className="font-semibold text-gray-900">{open ? '–' : time(today?.clockOutAt) || '–'}</dd>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <dt className="text-xs text-gray-500">Hours</dt>
                  <dd className="font-semibold text-gray-900">{formatHours(today?.hoursWorked ?? 0)}</dd>
                </div>
              </dl>

              {dayFlags(today).map((f) => (
                <div key={f.key} className={`text-sm border rounded-lg px-3 py-2 ${FLAG_TONE[f.tone]}`}>{f.label}</div>
              ))}

              {mayPunchHere && (open ? (
                <button
                  type="button"
                  onClick={() => setPunching('out')}
                  className="w-full flex items-center justify-center gap-2 px-5 py-4 bg-navy-800 text-white rounded-xl text-lg font-semibold hover:bg-navy-900"
                >
                  <LogOut className="w-5 h-5" aria-hidden="true" /> Punch out
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setPunching('in')}
                  className="w-full flex items-center justify-center gap-2 px-5 py-4 bg-emerald-600 text-white rounded-xl text-lg font-semibold hover:bg-emerald-700"
                >
                  <LogIn className="w-5 h-5" aria-hidden="true" /> Punch in
                </button>
              ))}

              {wide && (
                <button
                  type="button"
                  onClick={() => setCodeFor(open ? 'OUT' : 'IN')}
                  className={mayPunchHere
                    ? 'flex w-full items-center justify-center gap-2 px-5 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50'
                    : `w-full flex items-center justify-center gap-2 px-5 py-4 text-white rounded-xl text-lg font-semibold ${open ? 'bg-navy-800 hover:bg-navy-900' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                >
                  <Smartphone className={mayPunchHere ? 'w-4 h-4' : 'w-5 h-5'} aria-hidden="true" />
                  {mayPunchHere
                    ? 'Use my phone instead'
                    : open ? 'Punch out with your phone' : 'Punch in with your phone'}
                </button>
              )}

              {!mayPunchHere && (
                <p className="text-xs text-gray-500">
                  Attendance is recorded on your own phone, where the camera and the position are real.
                  Scan the code and the form opens there.
                </p>
              )}

              <p className="text-xs text-gray-500 flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" aria-hidden="true" />
                {hospital.configured
                  ? <span>Each punch takes a selfie and checks that you are within {hospital.radiusMeters} m of {hospital.name || 'the hospital'}.</span>
                  : <span>Each punch takes a selfie. The hospital's position has not been set yet, so punches are not checked against it.</span>}
              </p>
              {describeSchedule(data?.schedule) && (
                <p className="text-xs text-gray-500">Your hours: {describeSchedule(data.schedule)}.</p>
              )}
              <p className="urdu-text text-sm leading-loose text-gray-700" dir="rtl">
                ہر حاضری پر آپ کی تصویر اور آپ کے فون کا مقام درج ہوتا ہے۔ ہسپتال پہنچ کر حاضری لگائیں اور جاتے وقت چھٹی لگائیں۔
              </p>

              <div className="pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setMarkingForColleague(true)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-navy-700 hover:underline"
                >
                  <UserPlus className="w-4 h-4" aria-hidden="true" /> Mark for a colleague
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  For somebody who cannot reach their own account. They still scan it and take their own photo.
                </p>
              </div>
            </section>

            <section aria-label="This week" className="bg-white rounded-2xl shadow-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-gray-900">This week</h2>
                <span className="text-sm text-gray-600">{formatHours(data?.weekHours ?? 0)}</span>
              </div>
              <ul className="grid grid-cols-7 gap-1.5">
                {(data?.week || []).map((d) => {
                  const st = statusOf(d);
                  const isToday = d.date === today?.date;
                  return (
                    <li key={d.date} className="text-center">
                      <div className="text-[11px] text-gray-500">{datePart(d.date, { weekday: 'short' })}</div>
                      <div
                        title={describeDay(d)}
                        className={`mt-1 rounded-lg py-2 text-sm font-semibold ${st.cell} ${isToday ? 'ring-2 ring-navy-800' : ''}`}
                      >
                        {st.short || datePart(d.date, { day: 'numeric' })}
                      </div>
                      {d.hoursWorked > 0 && <div className="text-[11px] text-gray-500 mt-0.5">{formatHours(d.hoursWorked)}</div>}
                    </li>
                  );
                })}
              </ul>
              <p className="text-[11px] text-gray-500 mt-3">{STATUS_LEGEND}</p>
            </section>

            <MyLeaveCard />

            {data?.week?.some((d) => d.autoClosed && !d.corrected) && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>A day this week has no punch-out, so the system closed it. Ask an administrator to correct the time.</span>
              </div>
            )}
          </>
        )}
      </div>

      {punching && (
        <PunchDialog side={punching} hospital={hospital} onPunch={punch} onClose={() => setPunching(null)} />
      )}
      {codeFor && (
        <PunchCodeDialog
          side={codeFor}
          onDone={() => { setCodeFor(null); load(); }}
          onClose={() => { setCodeFor(null); load(); }}
        />
      )}
      {markingForColleague && (
        <MarkForColleagueDialog
          onDone={() => setMarkingForColleague(false)}
          onClose={() => setMarkingForColleague(false)}
        />
      )}
    </div>
  );
};

export default MyAttendance;
